import { Button, TextField } from '@material-ui/core';
import { Alert, Color } from '@material-ui/lab';
import React, { useContext, useRef, useState, useEffect } from 'react';
import { UIDialog } from '../../../../ui/feedback/UIDialog';
import Tooltip from '@material-ui/core/Tooltip';
import HelpIcon from '@material-ui/icons/Help';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AsyncAutocompleteSelectComponent, DropDownValue } from '../components/AsyncAutocompleteSelect';
import { DgCompliantPpsTableStructure } from "../indexes/DgCompliantPpsIndexes";
import { Compellable } from "../indexes/Indexes";
import { AppContext, AppContextSpec } from "../../App";

const useStyles = makeStyles((theme: Theme) => {
    return createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            width: '550px'
        },
        tooltip: {
            marginLeft: '15px'
        },
        sliderRoot: {
            display: 'flex',
            alignItems: 'center',
            width: '550px',
            margin: '10px 0 10px 0'
        },
        sliderTooltip: {
            marginLeft: 'auto'
        },
        displayFlex: {
            display: 'flex',
            alignItems: 'center',
            width: '550px'
        }
    });
});

export type EditFormModalOptions = {
    title: string;
    open: boolean;
    applyLabel: string;
    cancelLabel: string;
    applyAndResetLabel?: string;
    onClose: () => void;
    onSuccess: (shouldReset: boolean) => Promise<void>;
};

type EditFormProps<T> = {
    constraints: Compellable;
    record?: DgCompliantPpsTableStructure.RecordOf<Compellable>;
    modalOptions: EditFormModalOptions;
    onSubmit: (form: T) => Promise<string>;
};

type ValidationFeedback = {
    [key in keyof Compellable]: boolean;
};

export const EditForm = function <RecordType>({
                                                  constraints,
                                                  record,
                                                  onSubmit,
                                                  modalOptions,
                                              }: EditFormProps<RecordType>) {

    const FIELD_ORDER: string[] = ["channel", "store", "dgCompliantPps"];
    const tableFields = FIELD_ORDER.filter((key) =>
        Object.prototype.hasOwnProperty.call(constraints, key) && key !== 'id'
    );

    const initImmediateValidationFeedback = (resetOnSubmitAndContinue: boolean) => {
        const result: any = {};
        tableFields.map((field) => (result[field] = !!record || (!constraints[field].required || !constraints[field].editable || constraints[field].type === 'boolean' || resetOnSubmitAndContinue)));
        return result as ValidationFeedback;
    };

    const initFieldChangeState = () => {
        const isPristine = true;
        const result: any = {};
        tableFields.map((field) => (result[field] = record ? !isPristine : isPristine));
        return result as ValidationFeedback;
    };

    const buildInitialFormValues = () => ({
        channel: record?.channel ?? '',
        store: record?.store ?? '',
        dgCompliantPps: record?.dgCompliantPps ?? '',
    });

    const classes = useStyles();
    const appContext = useContext<AppContextSpec>(AppContext);

    const [isOk, setIsOk] = useState(initImmediateValidationFeedback(false));
    const $form = useRef<HTMLFormElement>(null);
    const [isPristine, setIsPristine] = useState(true);
    const [isFieldPristine, setIsFieldPristine] = useState(initFieldChangeState());
    const [internalNotification, setInternalNotification] = useState<
        { severity: Color; message: string } | undefined
    >();
    const [formValues, setFormValues] = useState<{ [key: string]: any }>(buildInitialFormValues());

    useEffect(() => {
        if (record) {
            const initialValues: any = {};
            tableFields.forEach((field) => {
                initialValues[field] = record[field];
            });
            setFormValues(initialValues);
        }
        // eslint-disable-next-line
    }, [record]);

    const resetStates = (resetOnSubmitAndContinue: boolean) => {
        setIsPristine(true);
        setIsOk(initImmediateValidationFeedback(resetOnSubmitAndContinue));
        setIsFieldPristine(initFieldChangeState());
        setInternalNotification(undefined);
        if (!resetOnSubmitAndContinue) {
            setFormValues(buildInitialFormValues());
        }
    };

    const handleModalClose = () => {
        modalOptions.onClose();
        resetStates(false);
    };

    const handleFormSubmit = (shouldReset: boolean) => {
        const form: any = {};
        let inputs = $form.current!.getElementsByTagName("input");

        for (let i = 0; i < inputs.length; i++) {
            form[inputs[i].id.replace("edit-", "")] = inputs[i].value;
        }

        setInternalNotification(undefined);

        onSubmit(form)
            .then(async (message) => {
                setInternalNotification({ severity: 'success', message });
                await modalOptions.onSuccess(shouldReset);
                if (shouldReset) {
                    resetStates(true);
                } else {
                    resetStates(false);
                }
            })
            .catch(({ message }) => {
                setInternalNotification({ severity: 'error', message });
            });
    };

    const handleImmediateValidation = (
        field: keyof Compellable,
        e: React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>
    ) => {
        const isRequired = constraints[field].required;
        const isEditable = constraints[field].editable;
        const type = constraints[field].type;
        const value = e.target.value.trim();

        const fieldValidation = (type: String, isRequired: boolean, isEditable: boolean, value: any) => {
            switch (type) {
                case ("boolean"): return true;
                case ("number"):
                    if (isNaN(parseInt(value))) return false;
                    return (isRequired && isEditable) ? '' !== value : true;
                default: return (isRequired && isEditable) ? '' !== value : true;
            }
        };

        // Reset dgCompliantPps when channel or store changes
        const shouldResetDgCompliantPps = field === 'channel' || field === 'store';

        setFormValues((prev) => ({
            ...prev,
            [field]: value,
            ...(shouldResetDgCompliantPps ? { dgCompliantPps: '' } : {}),
        }));

        setIsOk((prev) => ({
            ...prev,
            [field]: fieldValidation(type, isRequired, isEditable, value),
            ...(shouldResetDgCompliantPps ? { dgCompliantPps: false } : {}),
        }));

        setIsFieldPristine((prev) => ({
            ...prev,
            [field]: false,
            ...(shouldResetDgCompliantPps ? { dgCompliantPps: true } : {}),
        }));

        isPristine && setIsPristine(false);
        setInternalNotification(undefined);
    };

    const getDefaultValue = (field: string | number): DropDownValue | undefined => {
        if (record) {
            let key: string = constraints[field].reference;
            let splitedReferenceColumnLabel: string[] = constraints[field].referenceColumnLabel.split("_");

            let compositeValue = "";
            for (let position = 0; position < splitedReferenceColumnLabel.length; position++) {
                let columnLabel = splitedReferenceColumnLabel[position];
                let recordFieldName = key + columnLabel.charAt(0).toUpperCase() + columnLabel.slice(1);
                compositeValue += (compositeValue.length === 0)
                    ? record?.[recordFieldName]
                    : ' - ' + record?.[recordFieldName];
            }

            return {
                key: String(record?.[field]),
                value: String(compositeValue) === 'undefined' ? String(record?.[field]) : String(compositeValue)
            };
        }
        return undefined;
    };

    // Passes channel and store as parameters to dgCompliantPps dropdown
    // so the backend can filter stock locations by channel + store
    const dropdownParameters = (field: keyof Compellable): object => {
        if (field === 'dgCompliantPps') {
            return {
                channel: formValues?.channel?.split(' - ')[0],
                store: formValues?.store?.split(' - ')[0],
            };
        }
        return {};
    };

    const isDgCompliantPpsEnabled = !!formValues.channel && !!formValues.store;

    const buildModalActions = () => {
        const isButtonDisabled = isPristine || tableFields.some((field) => !isOk[field]);
        return [
            <Button key="modal-cancel" onClick={handleModalClose}>
                {modalOptions.cancelLabel}
            </Button>,
            modalOptions.applyAndResetLabel ? (
                <Button key="modal-apply-reset" onClick={handleFormSubmit.bind(null, true)} disabled={isButtonDisabled}>
                    {modalOptions.applyAndResetLabel}
                </Button>
            ) : null,
            <Button
                key="modal-validate"
                onClick={handleFormSubmit.bind(null, false)}
                variant="contained"
                color="primary"
                disabled={isButtonDisabled}
                disableElevation
            >
                {modalOptions.applyLabel}
            </Button>,
        ];
    };

    return (
        <UIDialog
            open={modalOptions.open}
            onClose={handleModalClose}
            title={modalOptions.title}
            actions={buildModalActions()}
        >
            {internalNotification && (
                <Alert severity={internalNotification.severity}>{internalNotification.message}</Alert>
            )}
            <form ref={$form} autoComplete={`off`}>
                {tableFields.map((field: keyof Compellable) => {
                    const isRequired = constraints[field].required;
                    const information = constraints[field].information;
                    const isEditable = constraints[field].editable;
                    const reference = constraints[field].reference;
                    const displayName = constraints[field].displayName;

                    if (reference && reference !== "") {
                        return (
                            <div key={field} className={classes.displayFlex}>
                                <AsyncAutocompleteSelectComponent
                                    scopeLimited={
                                        !appContext.appInfo?.scopes.includes('WRITE_ALL') &&
                                        !appContext.appInfo?.scopes.includes("HQ") &&
                                        !appContext.appInfo?.scopes.includes("READ_ALL")
                                    }
                                    id={`edit-${field}`}
                                    key={
                                        field === 'dgCompliantPps'
                                            ? `edit-dgCompliantPps-${formValues.channel}-${formValues.store}`
                                            : `edit-${field}`
                                    }
                                    autocompleteContainerStyle={{ flexGrow: 1 }}
                                    reference={reference}
                                    required={isRequired}
                                    parameters={dropdownParameters(field)}
                                    information={information}
                                    onSelect={handleImmediateValidation.bind(null, field)}
                                    label={displayName}
                                    // disabled={field === 'dgCompliantPps' && !isDgCompliantPpsEnabled}
                                    defaultValue={
                                        field === 'dgCompliantPps' && !formValues.dgCompliantPps
                                            ? undefined
                                            : getDefaultValue(field)
                                    }
                                />
                                {information && (
                                    <Tooltip title={information} className={classes.tooltip}>
                                        <HelpIcon />
                                    </Tooltip>
                                )}
                            </div>
                        );
                    } else {
                        return (
                            isEditable && (
                                <div key={field} className={classes.root}>
                                    <TextField
                                        key={field}
                                        required={isRequired}
                                        fullWidth
                                        error={!isFieldPristine[field] ? !isOk[field] : false}
                                        margin="normal"
                                        size="small"
                                        onInput={handleImmediateValidation.bind(null, field)}
                                        id={`edit-${field}`}
                                        label={displayName}
                                        variant="outlined"
                                        defaultValue={record ? record[field] : undefined}
                                    />
                                    {information && (
                                        <Tooltip title={information} className={classes.tooltip}>
                                            <HelpIcon />
                                        </Tooltip>
                                    )}
                                </div>
                            )
                        );
                    }
                })}
            </form>
        </UIDialog>
    );
};
